import { IMPORT_FIELDS } from '@homecare/domain';
import { z } from 'zod';
/**
 * CSV import (slice-01 §5, B3).
 *
 * The shape of the flow is the contract's job to enforce: **upload → preview →
 * confirm**, never upload → import → report. So there is no request that both
 * uploads and writes, and `confirm` takes a job id rather than a file. A single
 * "import this" endpoint would make the preview optional, and an optional
 * preview is one nobody uses on the day it matters.
 */
export const importFieldSchema = z.enum(IMPORT_FIELDS);
/**
 * Our field name to their column header.
 *
 * A partial record rather than a required one: an agency's export need not
 * carry a Medicare number, and requiring every key would make them map columns
 * that do not exist. Which fields are non-negotiable is
 * `REQUIRED_IMPORT_FIELDS`, checked against the file's real headers rather than
 * against the shape of this object.
 */
export const columnMappingSchema = z.record(importFieldSchema, z.string().trim().max(200));
export const importStatusSchema = z.enum(['PREVIEWED', 'QUEUED', 'RUNNING', 'COMPLETED', 'FAILED']);
/** One row that will not import, in §26.2's register. */
export const importRowErrorSchema = z.object({
    /** 1-based, header included, so it matches what a spreadsheet shows. */
    row: z.number().int().positive(),
    /** Their column header, never our field name and never an index. */
    column: z.string(),
    value: z.string(),
    /** "13/45/2020 is not a date. Use YYYY-MM-DD or MM/DD/YYYY." */
    message: z.string(),
});
/**
 * What preview answers.
 *
 * Carries the headers and the suggested mapping as well as the errors, because
 * the first thing a person does with a new export is fix the mapping — and
 * making them re-upload to see the effect of a mapping change is the difference
 * between a five-minute job and an afternoon.
 */
export const importPreviewSchema = z.object({
    jobId: z.string().uuid(),
    filename: z.string(),
    status: importStatusSchema,
    /** The file's own header row, in order. */
    sourceHeaders: z.array(z.string()),
    /** What was used for this preview: remembered, suggested, or supplied. */
    mapping: columnMappingSchema,
    /**
     * Whether the mapping came from the agency's remembered one.
     *
     * Shown so somebody who has imported before knows the mapping was not guessed
     * afresh, and somebody importing for the first time knows it was.
     */
    mappingRemembered: z.boolean(),
    /**
     * Problems with the mapping itself, as opposed to with rows.
     *
     * Reported separately because a required field with no column produces the
     * same error on all 200 rows, and 200 copies of one fact buries it.
     */
    mappingProblems: z.array(z.string()),
    totalRows: z.number().int().nonnegative(),
    validRows: z.number().int().nonnegative(),
    failedRows: z.number().int().nonnegative(),
    /** Capped for the response; the full set is in the error CSV. */
    errors: z.array(importRowErrorSchema),
    errorsTruncated: z.boolean(),
    /** The first few rows as they would be written. Shows the mapping working. */
    sample: z.array(z.record(z.string(), z.string().nullable())),
});
/**
 * Confirm.
 *
 * `mapping` is optional: preview already stored one, and a confirm that had to
 * repeat it would let the two disagree. Supplying it means "I changed the
 * mapping after previewing", which re-validates rather than trusting the
 * preview.
 */
export const confirmImportSchema = z.object({
    mapping: columnMappingSchema.optional(),
    /**
     * Remember this mapping for next time. §5 asks for it; making it explicit
     * means an experiment with an odd file does not overwrite the mapping the
     * agency's real export uses.
     */
    rememberMapping: z.boolean().default(true),
});
/** The result summary, and the job row generally. */
export const importJobSchema = z.object({
    id: z.string().uuid(),
    filename: z.string(),
    status: importStatusSchema,
    totalRows: z.number().int().nonnegative(),
    createdRows: z.number().int().nonnegative(),
    updatedRows: z.number().int().nonnegative(),
    failedRows: z.number().int().nonnegative(),
    errors: z.array(importRowErrorSchema),
    errorsTruncated: z.boolean(),
    /** Set only when the job itself broke, not when rows failed validation. */
    failureReason: z.string().nullable(),
    requestedBy: z.string().uuid(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * Where the failed rows come back from, as a CSV with an appended error
     * column. Null while there is nothing to download.
     */
    errorCsvUrl: z.string().nullable(),
});
export const importJobListSchema = z.object({ jobs: z.array(importJobSchema) });
/** The remembered mapping, for the mapping screen. */
export const importMappingSchema = z.object({
    mapping: columnMappingSchema,
    updatedAt: z.string().nullable(),
});
//# sourceMappingURL=import.js.map